// FirstPromoter referral attribution helper.
//
// Reads the `_fprom_tid` cookie (set by https://cdn.firstpromoter.com/fpr.js
// after a visitor arrives through a referral link, e.g.
//   https://zynthoro.ai?fpr=<token>&test_mode=1 )
// and returns it only when:
//   1. The visitor has granted the "marketing" cookie category in
//      the Zynthoro consent manager (`zy_cookie_prefs_v1.marketing === true`).
//   2. The current page has not been flagged as a marketing-denied route
//      (`window.__zyMarketingDeniedForPage !== true`).
//   3. The cookie value matches the strict `[A-Za-z0-9_-]{1,128}` pattern
//      enforced by the backend `fp_tid` field.
//
// Returning `null` means "no referral to attribute" — callers must omit the
// `fp_tid` field from the request body (not send `null`) so the backend
// treats it as absent.

const FPTID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

function marketingConsentGranted() {
  if (typeof window === "undefined") return false;
  if (window.__zyMarketingDeniedForPage === true) return false;
  try {
    const prefs = JSON.parse(localStorage.getItem("zy_cookie_prefs_v1") || "null");
    return !!prefs && prefs.marketing === true;
  } catch {
    return false;
  }
}

function readCookieTid() {
  if (typeof document === "undefined") return null;
  const part = document.cookie
    .split("; ")
    .find((p) => p.startsWith("_fprom_tid="));
  if (!part) return null;
  try {
    return decodeURIComponent(part.slice("_fprom_tid=".length));
  } catch {
    return null;
  }
}

export function readFpTid() {
  if (!marketingConsentGranted()) return null;
  const fromSdk = typeof window !== "undefined" && typeof window.FPROM?.data?.tid === "string"
    ? window.FPROM.data.tid
    : null;
  const tid = fromSdk || readCookieTid();
  if (!tid) return null;
  return FPTID_PATTERN.test(tid) ? tid : null;
}

// Spread into an axios POST body to add fp_tid only when a valid referral
// is present. Example:
//   axios.post(url, { plan_key, ...withFpTid() })
export function withFpTid() {
  const tid = readFpTid();
  return tid ? { fp_tid: tid } : {};
}
